import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    fetchAppointmentDetails, 
    handleDeleteAppointment, 
    fetchUserAppointments, 
    sortAppointmentsForPatient,
    viewChat
} from '../../appointment/appointmentFunctions.js';
import { markRead, updateRoomNotificationCount, getRoomUnreadMsgCount } from '../../matrix/matrixFuntions.js';
import { clearAppointmentsFromStorage } from '../utils/storageUtils.js';
import { toast } from 'react-toastify';
import { authenticateMatrixClient } from '../../../services/matrixAuthService.js';

export const usePatientAppointmentActions = (loggedInUser, token) => {
    const dispatch = useDispatch();
    const selectedAppointment = useSelector(state => state.appointmentDetails?.selectedAppointment);
    const currentPageNum = useSelector(state => state.appointmentDetails?.currentPageNumber);
    const chatDetails = useSelector(state => state.chatDetails);
    const roomsNotificationCount = useSelector(state => state.appointmentDetails?.roomsNotificationCount);
    const isSortAsc = useSelector(state => state.appointmentDetails?.sortAscending ?? true);
    
    const [showDeleteAppointment, setShowDeleteAppointment] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showApptDetails, setShowApptDetails] = useState(false);
    const [currentlyOpenAppointmentId, setCurrentlyOpenAppointmentId] = useState("");

    const handleApptDetailsShow = (appt) => {
        fetchAppointmentDetails(appt._id, dispatch, token);
        if (!showDeleteAppointment) {
            setShowApptDetails(true);
        }
    };

    const handleApptDetailsClose = () => {
        setShowApptDetails(false);
    };

    const handleDeleteApptShow = (appt) => {
        dispatch({
            type: 'selectAppointment',
            payload: appt
        });
        setShowDeleteAppointment(true);
    };

    const handleDeleteApptClose = () => {
        setShowDeleteAppointment(false);
        dispatch({
            type: 'selectAppointment',
            payload: null
        });
        dispatch({
            type: 'clearMsg'
        });
    };

    const handleDeleteApptSubmit = () => {
        setIsDeleting(true);
        const isDeleted = handleDeleteAppointment(selectedAppointment, loggedInUser, dispatch, token);
        if (isDeleted) {
            handleDeleteApptClose();
            fetchUserAppointments(loggedInUser._id, currentPageNum - 1, dispatch, token);
            // Clear cache after deletion to ensure fresh data
            clearAppointmentsFromStorage();
        }
        setIsDeleting(false);
    };

    const showNearestPracticesDiv = (appt) => {
        console.log('=== Clinic Click - Practice Details ===');
        console.log('Appointment ID:', appt._id);
        console.log('Appointment Service Type:', appt.service_type);
        console.log('Appointment Status:', appt.patient_appointment_status);
        console.log('Count of Practice Matches:', appt.count_of_practice_matches);
        console.log('Nearest Practices:', appt.nearest_practices);
        
        if (appt.nearest_practices && appt.nearest_practices.length > 0) {
            console.log('=== Individual Practice Details ===');
            appt.nearest_practices.forEach((practice, index) => {
                console.log(`--- Practice ${index + 1} ---`);
                console.log('Service ID:', practice.service_id);
                console.log('Location Name:', practice.location_name);
                console.log('Practice Appointment Status:', practice.practice_appointment_status);
                console.log('Synapse Room ID:', practice.synapse_room_id);
                console.log('Last Updated By User:', practice.last_updated_by_user);
                console.log('Last Updated DateTime:', practice.last_updated_date_time);
                console.log('Staff Pinned:', practice.staff_pinned);
                console.log('Staff Viewed:', practice.staff_viewed);
                console.log('--- End Practice Details ---');
            });
        } else {
            console.log('No nearest practices found for this appointment');
        }
        
        console.log('=== End Clinic Click Logging ===');
        
        if (currentlyOpenAppointmentId !== "" && currentlyOpenAppointmentId !== appt._id) {
            const element = document.getElementById(`practices-${currentlyOpenAppointmentId}`);
            if (element) {
                element.style.display = "none";
            }
        }
        setCurrentlyOpenAppointmentId(appt._id);
        fetchAppointmentDetails(appt._id, dispatch, token);
        dispatch({
            type: 'selectAppointment',
            payload: appt
        });
        const element = document.getElementById(`practices-${appt._id}`);
        if (element) {
            element.style.display = "block";
        }
        handleApptDetailsClose();
    };

    const closeNearestPracticesDiv = (appt) => {
        setCurrentlyOpenAppointmentId("");
        const element = document.getElementById(`practices-${appt._id}`);
        if (element) {
            element.style.display = "none";
        }
        dispatch({
            type: 'clearMsg'
        });
    };

    const handleViewChat = async (appt, practice) => {
        let matrixClient = chatDetails.matrixClient;
        if (!matrixClient) {
            try {
                matrixClient = await authenticateMatrixClient(loggedInUser, dispatch);
            } catch (error) {
                toast.error('Unable to connect to chat. Please try again.');
                return;
            }
        }
        if (!matrixClient) {
            toast.error('Chat is unavailable because Matrix authentication did not complete.');
            return;
        }

        console.log('=== PATIENT CHAT CLICK - RESET UNREAD COUNT ===');
        console.log('Appointment:', appt);
        console.log('Practice:', practice);
        console.log('Synapse Room ID:', practice.synapse_room_id);
        console.log('Current unread count before reset:', getRoomUnreadMsgCount(practice.synapse_room_id, roomsNotificationCount));
        
        // Ensure appointment has complete nearest_practices data
        const appointmentWithPractices = {
            ...appt,
            nearest_practices: appt.nearest_practices || [practice]
        };
        
        dispatch({
            type: 'selectAppointment',
            payload: appointmentWithPractices
        });
        
        console.log('Matrix client available, preparing chat room...');
        const selectedRoom = matrixClient.getRoom(practice.synapse_room_id);
        console.log('Selected room from store:', selectedRoom);

        dispatch({
            type: 'setSelectedChatRoom',
            payload: selectedRoom
        });

        if (selectedRoom) {
            // Mark messages as read in Matrix
            markRead(loggedInUser, matrixClient, selectedRoom);
        }

        // Reset the badge even if the room is not yet present in the local Matrix store.
        updateRoomNotificationCount(practice.synapse_room_id, 0, dispatch);
        
        console.log('=== END PATIENT CHAT CLICK - RESET UNREAD COUNT ===');
        
        try {
            console.log('About to call viewChat function...');
            viewChat(appointmentWithPractices, loggedInUser, practice, dispatch);
            console.log('viewChat function called successfully');
        } catch (error) {
            console.error('Error calling viewChat function:', error);
        }
    };

    const sortByDate = () => {
        dispatch({
            type: 'setSortAscending',
            payload: !isSortAsc
        });
        sortAppointmentsForPatient(loggedInUser._id, dispatch, token, "date", !isSortAsc);
    };

    return {
        // State
        showDeleteAppointment,
        isDeleting,
        showApptDetails,
        currentlyOpenAppointmentId,
        selectedAppointment,
        isSortAsc,
        
        // Actions
        handleApptDetailsShow,
        handleApptDetailsClose,
        handleDeleteApptShow,
        handleDeleteApptClose,
        handleDeleteApptSubmit,
        showNearestPracticesDiv,
        closeNearestPracticesDiv,
        handleViewChat,
        sortByDate
    };
};
